import type { StatWithId } from '../../types';
import { formatCalculatedStat } from '../../utils/statCalculations';

const BATTING_CALC_KEYS = ['AVG', 'OBP', 'SLG', 'OPS'];
const PITCHING_CALC_KEYS = ['ERA', 'WHIP'];
const PITCHING_RAW_KEYS = new Set(['IP', 'W', 'L', 'SV', 'ER', 'K', 'H_Allowed', 'R_Allowed', 'BB_Allowed', 'HBP_Allowed', 'BF', 'Pitches', 'WP', 'Balk']);

/** Compact one-game stats summary for accordion on game details page only. Uses tiles; hides pitching section when no pitching data. */
export function GameDetailsPlayerStatsSummary({
  rawStats,
  calculated,
  sport,
}: {
  playerId: string;
  rawStats: StatWithId[];
  calculated: Record<string, number>;
  sport?: string;
}) {
  const isBaseballOrSoftball = sport === 'Baseball' || sport === 'Softball';
  const hasAny = rawStats.length > 0 || Object.keys(calculated).length > 0;
  if (!hasAny) {
    return <p className="text-xs text-gray-500">No stats recorded for this game.</p>;
  }
  const rawByKey = rawStats.reduce((acc, s) => {
    acc[s.stat_key] = (acc[s.stat_key] || 0) + s.stat_value;
    return acc;
  }, {} as Record<string, number>);

  const battingCalc = BATTING_CALC_KEYS.filter((k) => calculated[k] != null);
  const pitchingCalc = PITCHING_CALC_KEYS.filter((k) => calculated[k] != null);
  const rawBattingKeys = Object.keys(rawByKey).filter((k) => !PITCHING_RAW_KEYS.has(k) && !PITCHING_CALC_KEYS.includes(k)).sort();
  const rawPitchingKeys = Object.keys(rawByKey).filter((k) => PITCHING_RAW_KEYS.has(k)).sort();
  const hasPitchingData =
    pitchingCalc.length > 0 || rawPitchingKeys.some((k) => (rawByKey[k] ?? 0) !== 0);

  const formatStatLabel = (key: string) =>
    key === 'Pass20Plus' ? 'Pass 20+' : key;

  const battingRows: Array<{ label: string; value: string }> = [
    ...battingCalc.map((k) => ({ label: formatStatLabel(k), value: formatCalculatedStat(calculated[k], k) })),
    ...rawBattingKeys.map((k) => ({ label: formatStatLabel(k), value: String(Number(rawByKey[k])) })),
  ];
  const formatPitchingLabel = (statKey: string) =>
    statKey === 'Pitches' ? 'Total' : statKey.replace(/_Allowed$/, '');
  const pitchingRows: Array<{ key: string; label: string; value: string }> = [
    ...pitchingCalc.map((k) => ({ key: k, label: k, value: formatCalculatedStat(calculated[k], k) })),
    ...rawPitchingKeys.map((k) => ({ key: k, label: formatPitchingLabel(k), value: String(Number(rawByKey[k])) })),
  ];

  return (
    <div className="text-gray-700 space-y-3">
      {battingRows.length > 0 && (
        <div className="rounded bg-gray-50/90 border border-gray-200/80 overflow-hidden">
          <div className="px-2.5 py-1.5 border-b border-gray-200/80 bg-gray-100/80">
            <span className="text-xs font-semibold text-gray-700">{isBaseballOrSoftball ? 'Batting' : 'Stats'}</span>
          </div>
          <div className="px-2.5 py-1.5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
              {battingRows.map(({ label, value }) => (
                <div key={label} className="flex flex-col py-0.5 min-w-0">
                  <span className="text-xs text-gray-500 truncate">{label}</span>
                  <span className="tabular-nums font-semibold text-gray-900 text-sm">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {hasPitchingData && pitchingRows.length > 0 && (
        <div className="rounded bg-gray-50/90 border border-gray-200/80 overflow-hidden">
          <div className="px-2.5 py-1.5 border-b border-gray-200/80 bg-gray-100/80">
            <span className="text-xs font-semibold text-gray-700">{isBaseballOrSoftball ? 'Pitching' : 'Stats'}</span>
          </div>
          <div className="px-2.5 py-1.5">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-1">
              {pitchingRows.map(({ key, label, value }) => (
                <div key={key} className="flex flex-col py-0.5 min-w-0">
                  <span className="text-xs text-gray-500 truncate">{label}</span>
                  <span className="tabular-nums font-semibold text-gray-900 text-sm">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
